import type { Ref } from 'react';
import { SectionHeader } from '@/components/ui';
import type { LiveEventCardDto, MarketOptionDto } from '@/lib/models';
import type { OrderSide } from '@/analytics';
import { BinaryQuestionCard } from '../BinaryMarkets/BinaryQuestionCard';
import { SportsMoneylineCard } from '../SportsMarkets/SportsMoneylineCard';

export interface LiveGroupSectionProps {
  /** Section title — "Closing Soon" / "Today" / "This Week". */
  title: string;
  /** Anchor target for the group chips (carries scroll-margin for the sticky top bar). */
  sectionRef: Ref<HTMLElement>;
  /** The group's cards, already pinned-first + volume ordered by the server. */
  events: LiveEventCardDto[];
  /** The lobby card grid (md:2 / xl:3 columns). */
  gridClassName: string;
  onOpenBuy: (event: LiveEventCardDto, option: MarketOptionDto, side: OrderSide) => void;
}

/**
 * One Live-page time group: a {@link SectionHeader} + the standard lobby
 * cards (sports moneyline vs binary chosen exactly as the lobby feed does).
 * Returns null when empty — the section, header, and its anchor chip all hide
 * together (spec §5.3).
 */
export function LiveGroupSection({ title, sectionRef, events, gridClassName, onOpenBuy }: LiveGroupSectionProps) {
  if (events.length === 0) return null;

  return (
    <section ref={sectionRef} className="flex w-full scroll-mt-24 flex-col gap-3">
      <SectionHeader title={title} />
      <div className={gridClassName}>
        {events.map((event) =>
          event.sportsMoneyline ? (
            <SportsMoneylineCard
              key={event.id}
              event={event}
              moneyline={event.sportsMoneyline}
              onOpenBuy={(option, side) => onOpenBuy(event, option, side)}
            />
          ) : (
            <BinaryQuestionCard key={event.id} event={event} onOpenBuy={(option, side) => onOpenBuy(event, option, side)} />
          ),
        )}
      </div>
    </section>
  );
}
